import { useCallback, useRef } from 'react';
import {
  dbui_v1_VerifyAuthResponse,
  DbuiV1RefreshTokenResponse,
} from '@antimatterhq/asl-api-ts-v1';
import { datadogLogs } from '@datadog/browser-logs';
import { refreshToken } from '@antimatterhq/ui/lib/authentication/refreshToken';
import { useInstanceId } from '@/contexts/InstanceIdContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useOptimisticUpdateVerifyAuth } from '@/hooks/dbui/useVerifyAuth';

const ENABLE_AUTH_LOCAL_DEBUG_LOGGING = false;

function redirectToAuthentication(instanceID: string, API_URL: string) {
  const returnPage = encodeURIComponent(window.location.href.split('?')[0]);
  const apiHost = new URL(API_URL).host;
  if (window.location.host === apiHost) {
    throw new Error('API URL is not valid');
  }

  window.location.href = `${API_URL}/apis/dbui/v1/authenticate?instanceID=${instanceID}&returnPage=${returnPage}`;
}

/**
 * Custom hook for handling authentication for the client.
 * Provides functions to initiate the authentication flow or refresh the existing token.
 *
 * Using `refreshAuthentication` over `initiateAuthentication` should be good enough for most cases as it handles the refreshing and will redirect to the login page if the token is expired.
 */
export const useClientAuthentication = () => {
  const optimisticUpdateVerifyAuth = useOptimisticUpdateVerifyAuth();
  const { API_URL } = useConfig();
  const { instanceId } = useInstanceId();
  const refreshPromiseRef = useRef<Promise<void> | null>(null);

  /**
   * Initiates the authentication process by redirecting the user to the authentication service.
   *
   * Using `refreshAuthentication` should be good enough for most cases as it handles the refreshing and will redirect to the login page if the token is expired.
   * @param instanceId - Optional instance ID to use for authentication. Defaults to the instance ID from context.
   */
  const initiateAuthentication = useCallback(() => {
    if (instanceId == null) {
      throw new Error('InstanceIdContext not found');
    }

    redirectToAuthentication(instanceId, API_URL);
  }, [instanceId, API_URL]);

  /**
   * Attempts to refresh the authentication token.
   * If successful, updates the local authentication state.
   * If unsuccessful (e.g., refresh token expired), initiates a new authentication flow.
   */
  const refreshAuthentication = useCallback(async (): Promise<void> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const doRefresh = async () => {
      if (instanceId == null) {
        throw new Error('InstanceIdContext not found during refresh');
      }
      try {
        if (ENABLE_AUTH_LOCAL_DEBUG_LOGGING) {
          console.warn('refreshing token (single call)');
        }
        datadogLogs.logger.info('refreshing token (single call)');

        const refreshResponse =
          await refreshToken<DbuiV1RefreshTokenResponse>(API_URL);
        if (refreshResponse) {
          optimisticUpdateVerifyAuth((prev) => {
            return {
              ...prev,
              expiresAt: refreshResponse.expiry,
              isDatabricksAuthed: true,
              authToken: refreshResponse.token,
            } as dbui_v1_VerifyAuthResponse;
          });
          if (ENABLE_AUTH_LOCAL_DEBUG_LOGGING) {
            console.warn('token refreshed');
          }
          datadogLogs.logger.info('token refreshed');
        } else {
          if (ENABLE_AUTH_LOCAL_DEBUG_LOGGING) {
            console.error('failed to refresh token (no data), redirecting...');
          }
          datadogLogs.logger.error(
            'failed to refresh token (no data), redirecting...',
          );
          initiateAuthentication();
          throw new Error('Failed to refresh token');
        }
      } catch (err) {
        if (ENABLE_AUTH_LOCAL_DEBUG_LOGGING) {
          console.error('failed to refresh token (catch), redirecting...', err);
        }
        datadogLogs.logger.error(
          'failed to refresh token (catch), redirecting...',
          undefined,
          err as Error,
        );
        initiateAuthentication();
      } finally {
        refreshPromiseRef.current = null;
      }
    };

    refreshPromiseRef.current = doRefresh();
    return refreshPromiseRef.current;
  }, [initiateAuthentication, instanceId, optimisticUpdateVerifyAuth, API_URL]);

  return {
    /** Attempts to refresh the authentication token; redirects to login if refresh fails. */
    refreshAuthentication,
  };
};
